import { motion } from "framer-motion";
import { Bold, Italic, Scan, Strikethrough, Underline, X } from "lucide-react";
import { useEffect, useState } from "react";
import ToggleButton from "../../ToggleButton";

const NumericSliderToolbar = ({
  questionId,
  isLeftSidebarOpen,
  leftSidebarWidth = 0,
  isRightSidebarOpen,
  windowWidth,
  selectedComponent,
  onComponentUpdate,
  onDeselect,
}) => {
  const [isMobile, setIsMobile] = useState(windowWidth < 768);
  const [fontSizeInput, setFontSizeInput] = useState(
    String(selectedComponent?.fontSize ?? 14)
  );
  const [optionBorderRadiusInput, setOptionBorderRadiusInput] = useState(
    String(selectedComponent?.optionBorderRadius ?? 8)
  );
  const [minValueInput, setMinValueInput] = useState(
    String(selectedComponent?.minValue ?? 0)
  );
  const [maxValueInput, setMaxValueInput] = useState(
    String(selectedComponent?.maxValue ?? 100)
  );
  const [targetValueInput, setTargetValueInput] = useState(
    String(selectedComponent?.targetValue ?? 50)
  );
  const [thicknessInput, setThicknessInput] = useState(
    String(selectedComponent?.thickness ?? 0)
  );

  const isVisible = selectedComponent?.showCurrentValue ?? true;

  const setIsVisible = (newValue) => {
    onComponentUpdate(questionId, selectedComponent.id, {
      showCurrentValue: newValue,
    });
  };

  const fontFamilies = [
    "Arial, sans-serif",
    "'Courier New', monospace",
    "'Times New Roman', serif",
    "'Comic Sans MS', cursive",
    "'Roboto', sans-serif",
  ];

  useEffect(() => {
    setIsMobile(windowWidth < 768);
  }, [windowWidth]);

  useEffect(() => {
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));
  }, [selectedComponent?.fontSize]);
  useEffect(() => {
    setOptionBorderRadiusInput(
      String(selectedComponent?.optionBorderRadius ?? 8)
    );
  }, [selectedComponent?.optionBorderRadius]);
  useEffect(() => {
    setMinValueInput(String(selectedComponent?.minValue ?? 0));
  }, [selectedComponent?.minValue]);
  useEffect(() => {
    setMaxValueInput(String(selectedComponent?.maxValue ?? 100));
  }, [selectedComponent?.maxValue]);
  useEffect(() => {
    setTargetValueInput(String(selectedComponent?.targetValue ?? 50));
  }, [selectedComponent?.targetValue]);
  useEffect(() => {
    setThicknessInput(String(selectedComponent?.thickness ?? 0));
  }, [selectedComponent?.thickness]);

  const calculatePosition = () => {
    if (windowWidth < 768) {
      return {
        left: "50%",
        transform: "translateX(-50%)",
      };
    }
    const leftSidebarOffset = isLeftSidebarOpen ? leftSidebarWidth / 2 : 0;
    const rightSidebarOffset = isRightSidebarOpen ? -32 : 0;
    const totalOffset = leftSidebarOffset + rightSidebarOffset;
    const leftOffset =
      totalOffset !== 0 ? `calc(50% + ${totalOffset}px)` : "50%";

    return {
      left: leftOffset,
      transform: "translateX(-50%)",
    };
  };
  const toggleFontStyle = (key) => {
    onComponentUpdate(questionId, selectedComponent.id, {
      fontStyles: {
        ...selectedComponent.fontStyles,
        [key]: !selectedComponent.fontStyles?.[key],
      },
    });
  };

  const position = calculatePosition();

  const clampNumber = (value, min, max, fallback) => {
    const parsed = parseInt(String(value), 10);
    if (!Number.isFinite(parsed)) return fallback;
    return Math.min(max, Math.max(min, parsed));
  };

  const commitFontSize = () => {
    const clamped = clampNumber(fontSizeInput, 8, 48, 14);
    setFontSizeInput(String(clamped));
    if (selectedComponent?.fontSize !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        fontSize: clamped,
      });
    }
  };
  const resetFontSizeInput = () =>
    setFontSizeInput(String(selectedComponent?.fontSize ?? 14));

  const commitOptionBorderRadius = () => {
    const clamped = clampNumber(optionBorderRadiusInput, 0, 50, 8);
    setOptionBorderRadiusInput(String(clamped));
    if (selectedComponent?.optionBorderRadius !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        optionBorderRadius: clamped,
      });
    }
  };
  const resetOptionBorderRadiusInput = () =>
    setOptionBorderRadiusInput(
      String(selectedComponent?.optionBorderRadius ?? 8)
    );

  const commitMinValue = () => {
    const parsed = parseInt(String(minValueInput), 10);
    if (!Number.isFinite(parsed)) {
      setMinValueInput(String(selectedComponent?.minValue ?? 0));
      return;
    }
    let nextMin = parsed;
    let nextMax = selectedComponent?.maxValue ?? 100;
    if (nextMin > nextMax) nextMax = nextMin;
    let nextTarget = selectedComponent?.targetValue ?? 50;
    if (nextTarget < nextMin) nextTarget = nextMin;
    if (nextTarget > nextMax) nextTarget = nextMax;
    setMinValueInput(String(nextMin));
    onComponentUpdate(questionId, selectedComponent.id, {
      minValue: nextMin,
      maxValue: nextMax,
      targetValue: nextTarget,
    });
  };
  const resetMinValueInput = () =>
    setMinValueInput(String(selectedComponent?.minValue ?? 0));

  const commitMaxValue = () => {
    const parsed = parseInt(String(maxValueInput), 10);
    if (!Number.isFinite(parsed)) {
      setMaxValueInput(String(selectedComponent?.maxValue ?? 100));
      return;
    }
    let nextMax = parsed;
    let nextMin = selectedComponent?.minValue ?? 0;
    if (nextMax < nextMin) nextMin = nextMax;
    let nextTarget = selectedComponent?.targetValue ?? 50;
    if (nextTarget < nextMin) nextTarget = nextMin;
    if (nextTarget > nextMax) nextTarget = nextMax;
    setMaxValueInput(String(nextMax));
    onComponentUpdate(questionId, selectedComponent.id, {
      minValue: nextMin,
      maxValue: nextMax,
      targetValue: nextTarget,
    });
  };
  const resetMaxValueInput = () =>
    setMaxValueInput(String(selectedComponent?.maxValue ?? 100));

  const commitTargetValue = () => {
    const parsed = parseInt(String(targetValueInput), 10);
    if (!Number.isFinite(parsed)) {
      setTargetValueInput(String(selectedComponent?.targetValue ?? 50));
      return;
    }
    const min = selectedComponent?.minValue ?? 0;
    const max = selectedComponent?.maxValue ?? 100;
    const clamped = Math.min(max, Math.max(min, parsed));
    setTargetValueInput(String(clamped));
    if (selectedComponent?.targetValue !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        targetValue: clamped,
      });
    }
  };
  const resetTargetValueInput = () =>
    setTargetValueInput(String(selectedComponent?.targetValue ?? 50));

  const commitThickness = () => {
    const clamped = clampNumber(thicknessInput, 0, 20, 0);
    setThicknessInput(String(clamped));
    if (selectedComponent?.thickness !== clamped) {
      onComponentUpdate(questionId, selectedComponent.id, {
        thickness: clamped,
      });
    }
  };
  const resetThicknessInput = () =>
    setThicknessInput(String(selectedComponent?.thickness ?? 0));

  return (
    <div
      className="fixed z-50 transition-all duration-300"
      style={{
        left: position.left,
        transform: position.transform,
        bottom: isMobile ? "calc(34px + env(safe-area-inset-bottom, 0px))" : 34,
      }}
    >
      <motion.div
        key={questionId}
        initial={{ y: 50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className={`bg-white rounded-xl shadow-xl border border-gray-200 relative`}
        style={{
          width: isMobile ? "calc(100vw - 24px)" : "546px",
          maxWidth: "calc(100vw - 24px)",
          maxHeight: isMobile ? "40vh" : "none",
          overflowY: isMobile ? "auto" : "visible",
        }}
      >
        <button
          onClick={onDeselect}
          className={`${
            isMobile ? "top-2 right-2" : "-top-3 right-2"
          } absolute bg-white rounded-full ${
            isMobile ? "p-1" : "p-1.5"
          } shadow-md border border-gray-200`}
          aria-label="Close"
          title="Close"
        >
          <X size={isMobile ? 14 : 16} className="text-gray-700" />
        </button>

        <div className={`${isMobile ? "px-2 py-3" : "px-4 py-4"} w-full`}>
          <div className="flex flex-col md:flex-row gap-3">
            <div className="flex flex-col gap-2 flex-1">
              <p className="text-xs text-black font-semibold mb-1">
                Text Options
              </p>
              <div className="flex gap-1">
                <label className="flex-1">
                  <select
                    value={selectedComponent?.fontFamily || fontFamilies[0]}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        fontFamily: e.target.value,
                      })
                    }
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  >
                    {fontFamilies.map((f) => (
                      <option key={f} value={f}>
                        {f.split(",")[0]}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="w-12 md:w-10">
                  <input
                    type="number"
                    min={8}
                    max={48}
                    value={fontSizeInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setFontSizeInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= 8 &&
                        parsed <= 48 &&
                        selectedComponent?.fontSize !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          fontSize: parsed,
                        });
                      }
                    }}
                    onBlur={commitFontSize}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitFontSize();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetFontSizeInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>
              <div className="flex gap-1">
                <div className="flex gap-1">
                  {["bold", "italic", "underline", "lineThrough"].map(
                    (style) => {
                      const iconMap = {
                        bold: <Bold className="h-5 w-5" />,
                        italic: <Italic className="h-5 w-5" />,
                        underline: <Underline className="h-5 w-5" />,
                        lineThrough: <Strikethrough className="h-5 w-5" />,
                      };
                      const titleMap = {
                        bold: "Bold",
                        italic: "Italic",
                        underline: "Underline",
                        lineThrough: "Strikethrough",
                      };
                      const isActive = Boolean(
                        selectedComponent?.fontStyles?.[style]
                      );
                      return (
                        <button
                          key={style}
                          onClick={() => toggleFontStyle(style)}
                          className={`w-8 h-8 rounded border text-sm font-semibold flex items-center justify-center select-none
                      ${
                        isActive
                          ? "bg-indigo-600 text-white border-indigo-600"
                          : "bg-white text-gray-700 border-gray-300 hover:bg-gray-100"
                      }`}
                          title={titleMap[style]}
                          aria-pressed={isActive}
                          type="button"
                        >
                          {iconMap[style]}
                        </button>
                      );
                    }
                  )}
                </div>
                <div className="flex justify-center items-center w-8 h-8 rounded border overflow-hidden  shadow-lg">
                  <input
                    type="color"
                    value={selectedComponent?.optionTextColor || "#000000"}
                    onChange={(e) =>
                      onComponentUpdate(questionId, selectedComponent.id, {
                        optionTextColor: e.target.value,
                      })
                    }
                    className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 items-center">
                <label className="min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Corner Radius
                  </p>
                  <div className="flex items-center gap-2 px-2 w-full h-8 overflow-hidden rounded-md bg-gray-300">
                    <Scan className="h-5 w-5" />
                    <input
                      type="number"
                      min={0}
                      max={50}
                      value={optionBorderRadiusInput}
                      onChange={(e) => {
                        const next = e.target.value;
                        setOptionBorderRadiusInput(next);
                        const parsed = parseInt(next, 10);
                        if (
                          Number.isFinite(parsed) &&
                          parsed >= 0 &&
                          parsed <= 50 &&
                          selectedComponent?.optionBorderRadius !== parsed
                        ) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            optionBorderRadius: parsed,
                          });
                        }
                      }}
                      onBlur={commitOptionBorderRadius}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          commitOptionBorderRadius();
                        } else if (e.key === "Escape") {
                          e.preventDefault();
                          resetOptionBorderRadiusInput();
                        }
                      }}
                      className="w-10 border-none text-[11px] font-medium bg-transparent focus:outline-none"
                    />
                  </div>
                </label>

                <ToggleButton
                  isVisible={isVisible}
                  setIsVisible={setIsVisible}
                />
              </div>
            </div>
            <div className="flex flex-col gap-3 flex-1">
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                <label>
                  <p className="text-xs text-black font-semibold mb-1">
                    Slider Background Color
                  </p>
                  <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow ">
                    <input
                      type="color"
                      value={
                        selectedComponent?.sliderBackgroundColor || "#e5e7eb"
                      }
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          sliderBackgroundColor: e.target.value,
                        })
                      }
                      className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                </label>

                <label className="flex-1 min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Slider Color
                  </p>
                  <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow">
                    <input
                      type="color"
                      value={selectedComponent?.sliderColor || "#3b82f6"}
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          sliderColor: e.target.value,
                        })
                      }
                      className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                </label>
                <label>
                  <p className="text-xs text-black font-semibold mb-1">
                    Items Background Color
                  </p>
                  <div className="flex justify-center items-center w-full h-8 overflow-hidden rounded-md shadow">
                    <input
                      type="color"
                      value={
                        selectedComponent?.optionBackgroundColor || "#f9fafb"
                      }
                      onChange={(e) =>
                        onComponentUpdate(questionId, selectedComponent.id, {
                          optionBackgroundColor: e.target.value,
                        })
                      }
                      className="w-[calc(100%+8px)] h-14 -m-1 border-none p-0 focus:outline-none ring-0 outline-none"
                    />
                  </div>
                </label>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                <label className="flex-1 min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">Min</p>
                  <input
                    type="number"
                    value={minValueInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setMinValueInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        selectedComponent?.minValue !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          minValue: parsed,
                        });
                        const target = selectedComponent?.targetValue ?? 50;
                        if (target < parsed) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            targetValue: parsed,
                          });
                          setTargetValueInput(String(parsed));
                        }
                      }
                    }}
                    onBlur={commitMinValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitMinValue();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetMinValueInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>

                <label className="flex-1 min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">Max</p>
                  <input
                    type="number"
                    value={maxValueInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setMaxValueInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        selectedComponent?.maxValue !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          maxValue: parsed,
                        });
                        const target = selectedComponent?.targetValue ?? 50;
                        if (target > parsed) {
                          onComponentUpdate(questionId, selectedComponent.id, {
                            targetValue: parsed,
                          });
                          setTargetValueInput(String(parsed));
                        }
                      }
                    }}
                    onBlur={commitMaxValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitMaxValue();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetMaxValueInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>

                <label className="flex-1 min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Target
                  </p>
                  <input
                    type="number"
                    min={selectedComponent?.minValue || 0}
                    max={selectedComponent?.maxValue || 100}
                    value={targetValueInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setTargetValueInput(next);
                      const parsed = parseInt(next, 10);
                      const min = selectedComponent?.minValue ?? 0;
                      const max = selectedComponent?.maxValue ?? 100;
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= min &&
                        parsed <= max &&
                        selectedComponent?.targetValue !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          targetValue: parsed,
                        });
                      }
                    }}
                    onBlur={commitTargetValue}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitTargetValue();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetTargetValueInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
                <label className="flex-1 min-w-0">
                  <p className="text-xs text-black font-semibold mb-1">
                    Thickness
                  </p>
                  <input
                    type="number"
                    min={0}
                    max={20}
                    value={thicknessInput}
                    onChange={(e) => {
                      const next = e.target.value;
                      setThicknessInput(next);
                      const parsed = parseInt(next, 10);
                      if (
                        Number.isFinite(parsed) &&
                        parsed >= 0 &&
                        parsed <= 20 &&
                        selectedComponent?.thickness !== parsed
                      ) {
                        onComponentUpdate(questionId, selectedComponent.id, {
                          thickness: parsed,
                        });
                      }
                    }}
                    onBlur={commitThickness}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        commitThickness();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        resetThicknessInput();
                      }
                    }}
                    className="block w-full rounded-md border border-gray-300 p-1.5 text-[11px] focus:outline-none focus:ring-1 focus:ring-indigo-500"
                  />
                </label>
              </div>
            </div>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default NumericSliderToolbar;
